import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject, 
  listAll,
  getMetadata
} from 'firebase/storage';
import { storage } from './firebase';

// Supported image types
const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg', 
  'image/png',
  'image/webp',
  'image/gif'
];

// Maximum file size (5MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Upload a single image file to Firebase Storage
 */
export const uploadProductImage = async (
  file: File, 
  productId?: string
): Promise<string> => {
  try {
    // Validate file type
    if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
      throw new Error('Unsupported file type. Please use JPEG, PNG, WEBP, or GIF files.');
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('File size too large. Please use files smaller than 5MB.');
    }

    // Generate unique filename
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2);
    const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${timestamp}_${randomString}.${extension}`;

    // Determine storage path
    const storagePath = productId 
      ? `products/${productId}/${fileName}`
      : `products/temp/${fileName}`;

    // Create storage reference
    const storageRef = ref(storage, storagePath);

    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to upload image. Please try again.');
  }
};

/**
 * Upload multiple image files to Firebase Storage
 */
export const uploadProductImages = async (
  files: File[], 
  productId?: string
): Promise<string[]> => {
  try {
    const uploadPromises = files.map(file => uploadProductImage(file, productId));
    const urls = await Promise.all(uploadPromises);
    return urls;
  } catch (error) {
    console.error('Error uploading multiple images:', error);
    throw error;
  }
};

/**
 * Delete an image from Firebase Storage by URL
 */
export const deleteImageByUrl = async (imageUrl: string): Promise<void> => {
  try {
    // Extract the storage path from the URL
    const url = new URL(imageUrl);
    
    // Check if it's a Firebase Storage URL
    if (!url.hostname.includes('firebasestorage.googleapis.com')) {
      console.log('Not a Firebase Storage URL, skipping deletion:', imageUrl);
      return;
    }

    // Extract the file path from the URL
    const pathMatch = imageUrl.match(/\/o\/(.+?)\?/);
    if (!pathMatch) {
      console.log('Could not extract path from URL:', imageUrl);
      return;
    }

    const filePath = decodeURIComponent(pathMatch[1]);
    const storageRef = ref(storage, filePath);

    // Check if file exists before trying to delete
    try {
      await getMetadata(storageRef);
      await deleteObject(storageRef);
      console.log('Successfully deleted image:', filePath);
    } catch (error: any) {
      if (error.code === 'storage/object-not-found') {
        console.log('Image not found in storage, may have been already deleted:', filePath);
      } else {
        throw error;
      }
    }
  } catch (error) {
    console.error('Error deleting image:', error);
    // Don't throw error for deletion failures as it shouldn't block the main operation
  }
};

/**
 * Delete multiple images from Firebase Storage by URLs
 */
export const deleteImagesByUrls = async (imageUrls: string[]): Promise<void> => {
  try {
    const deletePromises = imageUrls.map(url => deleteImageByUrl(url));
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error deleting multiple images:', error);
    // Don't throw error for deletion failures
  }
};

/**
 * Move images from temp folder to product folder
 */
export const moveImagesToProduct = async (
  tempUrls: string[], 
  productId: string
): Promise<string[]> => {
  try {
    const newUrls: string[] = [];
    
    for (const tempUrl of tempUrls) {
      // Check if it's already in the product folder
      if (tempUrl.includes(`products/${productId}/`)) {
        newUrls.push(tempUrl);
        continue;
      }

      // Check if it's a temp URL that needs to be moved
      if (tempUrl.includes('products/temp/')) {
        const pathMatch = tempUrl.match(/\/o\/(.+?)\?/);
        if (pathMatch) {
          const tempPath = decodeURIComponent(pathMatch[1]);
          const fileName = tempPath.split('/').pop();
          
          if (fileName) {
            const newPath = `products/${productId}/${fileName}`;
            
            // Create references
            const oldRef = ref(storage, tempPath);
            const newRef = ref(storage, newPath);
            
            try {
              // Get the file data
              const metadata = await getMetadata(oldRef);
              const response = await fetch(tempUrl);
              const blob = await response.blob();
              
              // Upload to new location
              await uploadBytes(newRef, blob, { 
                contentType: metadata.contentType 
              });
              
              // Get new URL
              const newUrl = await getDownloadURL(newRef);
              newUrls.push(newUrl);
              
              // Delete old file
              await deleteObject(oldRef);
            } catch (error) {
              console.error('Error moving image:', error);
              // If move fails, keep the original URL
              newUrls.push(tempUrl);
            }
          } else {
            newUrls.push(tempUrl);
          }
        } else {
          newUrls.push(tempUrl);
        }
      } else {
        // Not a temp URL, keep as is
        newUrls.push(tempUrl);
      }
    }
    
    return newUrls;
  } catch (error) {
    console.error('Error moving images to product folder:', error);
    // Return original URLs if move fails
    return tempUrls;
  }
};

/**
 * Move images from temp folder to bundle folder
 */
export const moveImagesToBundle = async (
  tempUrls: string[],
  bundleId: string
): Promise<string[]> => {
  try {
    const newUrls: string[] = [];

    for (const tempUrl of tempUrls) {
      // Already in the bundle folder
      if (tempUrl.includes(`bundles/${bundleId}/`)) {
        newUrls.push(tempUrl);
        continue;
      }

      // Existing bundle image from another bundle - clone by copying into the new bundle folder
      if (tempUrl.includes('/bundles/')) {
        const pathMatch = tempUrl.match(/\/o\/(.+?)\?/);
        if (pathMatch) {
          const sourcePath = decodeURIComponent(pathMatch[1]);
          const fileName = sourcePath.split('/').pop();

          if (fileName) {
            const newPath = `bundles/${bundleId}/${fileName}`;
            const sourceRef = ref(storage, sourcePath);
            const destRef = ref(storage, newPath);

            try {
              const metadata = await getMetadata(sourceRef);
              const response = await fetch(tempUrl);
              const blob = await response.blob();

              await uploadBytes(destRef, blob, {
                contentType: metadata.contentType
              });

              const newUrl = await getDownloadURL(destRef);
              newUrls.push(newUrl);
              continue;
            } catch (error) {
              console.error('Error copying bundle image:', error);
              // Fall through to keeping original URL if copy fails
            }
          }
        }
      }

      // Detect temp uploads (reuse products/temp as used by ImageUpload) or generic temp bundles
      const isTemp = tempUrl.includes('products/temp/') || tempUrl.includes('bundles/temp/');
      if (isTemp) {
        const pathMatch = tempUrl.match(/\/o\/(.+?)\?/);
        if (pathMatch) {
          const tempPath = decodeURIComponent(pathMatch[1]);
          const fileName = tempPath.split('/').pop();

          if (fileName) {
            const newPath = `bundles/${bundleId}/${fileName}`;

            const oldRef = ref(storage, tempPath);
            const newRef = ref(storage, newPath);

            try {
              const metadata = await getMetadata(oldRef);
              const response = await fetch(tempUrl);
              const blob = await response.blob();

              await uploadBytes(newRef, blob, {
                contentType: metadata.contentType
              });

              const newUrl = await getDownloadURL(newRef);
              newUrls.push(newUrl);

              await deleteObject(oldRef);
            } catch (error) {
              console.error('Error moving bundle image:', error);
              // Keep original URL if move fails
              newUrls.push(tempUrl);
            }
          } else {
            newUrls.push(tempUrl);
          }
        } else {
          newUrls.push(tempUrl);
        }
      } else {
        // Not a temp URL, keep as is
        newUrls.push(tempUrl);
      }
    }

    return newUrls;
  } catch (error) {
    console.error('Error moving images to bundle folder:', error);
    return tempUrls;
  }
};

/**
 * Validate image file
 */
export const validateImageFile = (file: File): string | null => {
  if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    return 'Unsupported file type. Please use JPEG, PNG, WEBP, or GIF files.';
  }

  if (file.size > MAX_FILE_SIZE) {
    return 'File size too large. Please use files smaller than 5MB.';
  }

  return null;
};

/**
 * Get file size in human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}; 
